import React, {
  FC, ReactNode, useCallback, useMemo, useRef,
} from 'react';
import { Box, MenuItem } from '@mui/material';
import { AccountInfo } from '@azure/msal-browser';
import { Header as SharedHeader } from '../../shared/components/header';
import { msalInstance } from '../../shared/msal';
import { DepartmentSelector } from '../../pages/machinePage/planner/partials/DepartmentSelector';
import { Company } from '../company';
import { stopPropagation } from '../../shared/logic/functions';
import { SearchSelect } from '../../shared/components/searchSelect';
import { SelectItem } from '../../shared/types/util/selectItem';
import {
  useGetMyCompanyQuery,
  useSetMyCompanyMutation,
  useGetCompanyListQuery,
} from '../../shared/redux/machine';
import { useDispatch } from '../../core/hooks/redux';
import { reset as resetLeftFilters } from '../../core/redux/leftFilterState';
import { reset as resetViewFilters } from '../../core/redux/viewSetting';

export const Header: FC<{
  page?: 'project' | 'worker' | 'machine';
  children?: ReactNode;
}> = ({ page, children }) => {
  const account: AccountInfo | null = msalInstance.getActiveAccount();
  const { data: currentCompany, isFetching } = useGetMyCompanyQuery();
  const isCompanyLoading = isFetching && !currentCompany;
  const { data: companies } = useGetCompanyListQuery();
  const [setCompanyMutation] = useSetMyCompanyMutation();
  const dispatch = useDispatch();
  const closeMenuRef = useRef<(() => void) | undefined>(undefined);

  const getInitials = () => {
    let nameArray: string[] | null = null;
    if (account?.name) {
      nameArray = account.name.split(' ');
    }
    const firstName = nameArray ? nameArray[0] : null;
    const lastName = nameArray ? nameArray[nameArray.length - 1] : null;
    if (!firstName || !lastName) {
      // can not get username
      return 'N/A';
    }
    return firstName.charAt(0) + lastName.charAt(0);
  };

  const setCompany = useCallback(
    async (item: SelectItem | undefined) => {
      if (item) {
        await setCompanyMutation(String(item.id)).unwrap();
        dispatch(resetLeftFilters());
        dispatch(resetViewFilters());
        closeMenuRef.current?.();
      }
    },
    [dispatch, setCompanyMutation],
  );

  const companyItems: SelectItem[] = useMemo(
    () => (companies ?? []).map((c) => ({ id: c, label: c })),
    [companies],
  );

  const extraMenuItems = useMemo(
    () => (
      <MenuItem
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        sx={{ paddingTop: 1 }}
        disableRipple
        disableTouchRipple
      >
        <SearchSelect
          label="Selskap"
          value={
            currentCompany
              ? { id: currentCompany.name, label: currentCompany.name }
              : undefined
          }
          onChange={setCompany}
          loading={isFetching}
          size="small"
        >
          {companyItems}
        </SearchSelect>
      </MenuItem>
    ),
    [currentCompany, setCompany, companyItems, isFetching],
  );

  return (
    <SharedHeader
      page={page}
      initials={getInitials()}
      company={currentCompany}
      isCompanyLoading={isCompanyLoading}
      extraMenuItems={extraMenuItems}
      onCloseMenuReady={(closeMenu) => {
        closeMenuRef.current = closeMenu;
      }}
      middleContent={<DepartmentSelector />}
      rightMiddleContent={(
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          gap={4}
          paddingRight={2}
          sx={{ placeContent: 'end' }}
        >
          <Company />
        </Box>
    )}
    >
      {children}
    </SharedHeader>
  );
};
